import React, { useContext, useState } from 'react';
import { Button, Flex, Input, Select, Switch } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import EventSeverityIndicator from '@totem/components/services/threats/EventSeverityIndicator';
import NozomiEventConfigContext from '@totem/components/settings/sections/nozomi/eventConfiguration/nozomiEventConfigContext';
import {
  NozomiEventConfiguration,
  TypeItem,
  TypeItems,
} from '@totem/components/settings/sections/nozomi/eventConfiguration/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import IBDIcon from '@totem/styles/Icon';
import { isNotNull } from '@totem/utilities/common';

type Props = {
  canEdit: boolean;
  typeItems: TypeItems;
};

const EventTypeTable = ({ typeItems, canEdit }: Props) => {
  const { loading, data, setData, updateType } = useContext(
    NozomiEventConfigContext,
  );
  const [editItem, setEditItem] = useState<TypeItem>(null);
  const [editItemName, setEditItemName] = useState<string>('');

  const addType = () => {
    if (typeItems.findIndex((item) => item.name === '') < 0) {
      const newTypeItem: TypeItem = {
        name: '',
        import: false,
        eventSeverity: 2,
      };
      const updatedNozomiConfig: NozomiEventConfiguration = {
        ...data,
        organization: {
          ...data.organization,
          typeConfiguration: [
            ...data.organization.typeConfiguration,
            newTypeItem,
          ],
        },
      };

      setData(updatedNozomiConfig);

      setEditItem(newTypeItem);

      setEditItemName(newTypeItem.name);
      return;
    }
  };

  const removeType = (old: TypeItem) => {
    const updatedNozomiConfig: NozomiEventConfiguration = {
      ...data,
      organization: {
        ...data.organization,
        typeConfiguration: data.organization.typeConfiguration.filter(
          (item) => item.name !== old.name,
        ),
      },
    };

    setData(updatedNozomiConfig);
  };

  const columns: ColumnProps<TypeItem>[] = [
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (_, record: TypeItem) => {
        if (isNotNull(editItem) && editItemName === record.name) {
          return (
            <Input
              value={editItem.name}
              disabled={!canEdit}
              onChange={(event) => {
                setEditItem((prevState) => ({
                  ...prevState,
                  name: event.target.value,
                }));
              }}
            />
          );
        }
        return <span>{record.name}</span>;
      },
    },
    {
      title: 'Import',
      dataIndex: 'import',
      key: 'import',
      render: (_, record: TypeItem) => {
        if (isNotNull(editItem) && editItemName === record.name) {
          return (
            <Switch
              value={editItem.import}
              disabled={!canEdit}
              onChange={(newValue) => {
                setEditItem((prevState) => ({
                  ...prevState,
                  import: newValue,
                }));
              }}
            />
          );
        }
        return <span>{record.import ? 'True' : 'False'}</span>;
      },
    },
    {
      title: 'Event Severity',
      dataIndex: 'eventSeverity',
      key: 'eventSeverity',
      render: (_, record: TypeItem) => {
        if (isNotNull(editItem) && editItemName === record.name) {
          return (
            <Select
              value={editItem.eventSeverity}
              disabled={!canEdit}
              options={[
                {
                  label: 'Ok',
                  value: 0,
                },
                {
                  label: 'Information',
                  value: 1,
                },
                {
                  label: 'Warning',
                  value: 2,
                },
                {
                  label: 'Minor',
                  value: 3,
                },
                {
                  label: 'Major',
                  value: 4,
                },
                {
                  label: 'Critical',
                  value: 5,
                },
              ]}
              onChange={(newValue) => {
                setEditItem((prevState) => ({
                  ...prevState,
                  eventSeverity: newValue,
                }));
              }}
            />
          );
        }
        return <EventSeverityIndicator severity={record.eventSeverity} />;
      },
    },
    {
      title: (
        <Flex justify="space-between">
          <div>Action</div>
          <div>
            {canEdit && (
              <Button
                shape={'circle'}
                icon={<IBDIcon name={'Add'} title={'Add'} />}
                onClick={() => addType()}
              />
            )}
          </div>
        </Flex>
      ),
      width: '124px',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record: TypeItem) => {
        if (isNotNull(editItem) && editItemName === record.name) {
          return (
            <Flex justify="space-evenly">
              <Button
                shape={'circle'}
                icon={<IBDIcon name={'Save'} title={'Save'} />}
                onClick={() => {
                  updateType(record, editItem);

                  setEditItem(null);

                  setEditItemName('');
                }}
              />
              <Button
                shape={'circle'}
                icon={<IBDIcon name={'Cancel'} title={'Cancel'} />}
                onClick={() => {
                  setEditItem(null);

                  setEditItemName('');
                }}
              />
            </Flex>
          );
        }

        if (canEdit) {
          return (
            <Flex justify="space-evenly">
              <Button
                shape={'circle'}
                icon={<IBDIcon name={'Edit'} title={'Edit'} />}
                onClick={() => {
                  setEditItem(record);

                  setEditItemName(record.name);
                }}
              />
              <Button
                shape={'circle'}
                icon={<IBDIcon name={'Delete'} title={'Delete'} />}
                onClick={() => removeType(record)}
              />
            </Flex>
          );
        }

        return <span />;
      },
    },
  ];

  return (
    <IBTable
      showSorterTooltip
      columns={columns}
      dataSource={isNotNull(typeItems) ? typeItems : []}
      loading={loading}
      pagination={{
        current: 1,
        pageSize: 1000,
      }}
      rowKey={(record) => record.name}
    />
  );
};

export default EventTypeTable;
